import type { ReactNode } from 'react';
import type { User } from 'firebase/auth';
import type { 
  SSEEventType, 
  SSEConnectedEvent, 
  SSEHeartbeatEvent,
  MediaProcessingUpdate,
  MediaProcessingOptions 
} from 'src/types/event-source';

import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { useRef, useMemo, useState, useEffect, useContext, useCallback, createContext } from 'react';

import { mutatePlaceList } from 'src/utils/mutate-utils';

import { CONFIG } from 'src/config-global';
import { useEventSource } from 'src/contexts/event-source-context';

import { useWorkspaceContext } from 'src/components/workspace/use-workspace-context';

// ----------------------------------------------------------------------

interface MediaUpdatesContextType {
  // Connection state
  isConnected: boolean;
  isConnecting: boolean;
  connectionInfo: SSEConnectedEvent | undefined;
  error: Error | undefined;
  
  // Auth state
  user: User | null;
  isAuthReady: boolean;
  
  // Media updates
  mediaUpdates: MediaProcessingUpdate[];
  
  // Connection management
  connect: () => Promise<void>;
  
  // Subscription management
  subscribe: (callback: (update: MediaProcessingUpdate) => void) => () => void;
  subscribeToConnected: (callback: (event: SSEConnectedEvent) => void) => () => void;
  subscribeToHeartbeat: (callback: (event: SSEHeartbeatEvent) => void) => () => void;
  subscribeToError: (callback: (error: Error) => void) => () => void;
}

const MediaUpdatesContext = createContext<MediaUpdatesContextType | null>(null);

// ----------------------------------------------------------------------

interface MediaUpdatesProviderProps {
  children: ReactNode;
  options?: MediaProcessingOptions;
}

/**
 * MediaUpdatesProvider - A context provider that manages a single EventSource connection
 * for media processing updates that can be shared across multiple components.
 * 
 * Key features:
 * - Automatically connects when user is authenticated
 * - Disconnects when user logs out
 * - Reconnects automatically on connection errors
 * - Provides subscription system for components to listen to updates
 * - Manages auth state changes reactively
 */

export function MediaUpdatesProvider({ children, options = {} }: MediaUpdatesProviderProps) {
  const {
    reconnectInterval = 10000,
    maxReconnectAttempts = 50,
  } = options;

  const eventSource = useEventSource();
  const { orgGuid } = useWorkspaceContext();
  
  // Connection state
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionInfo, setConnectionInfo] = useState<SSEConnectedEvent | undefined>();
  const [error, setError] = useState<Error | undefined>();
  const [mediaUpdates, setMediaUpdates] = useState<MediaProcessingUpdate[]>([]);
  
  // Subscription management
  const mediaUpdateSubscribers = useRef<Set<(update: MediaProcessingUpdate) => void>>(new Set());
  const connectedSubscribers = useRef<Set<(event: SSEConnectedEvent) => void>>(new Set());
  const heartbeatSubscribers = useRef<Set<(event: SSEHeartbeatEvent) => void>>(new Set());
  const errorSubscribers = useRef<Set<(error: Error) => void>>(new Set());
  
  // Connection management
  const connectionIdRef = useRef<string>();
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  
  // Auth state management - refs for internal logic, state for context consumers
  const userRef = useRef<User | null>(null);
  const isAuthReadyRef = useRef(false);
  
  // Create stable connection ID
  if (!connectionIdRef.current) {
    connectionIdRef.current = `media_updates_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
  
  const connectionId = connectionIdRef.current;

  // Notify subscribers
  const notifyMediaUpdateSubscribers = useCallback((update: MediaProcessingUpdate) => {
    mediaUpdateSubscribers.current.forEach(callback => {
      try {
        callback(update);
      } catch (err) {
        console.error('Error in media update subscriber:', err);
      }
    });
  }, []);

  const notifyConnectedSubscribers = useCallback((event: SSEConnectedEvent) => {
    connectedSubscribers.current.forEach(callback => {
      try {
        callback(event);
      } catch (err) {
        console.error('Error in connected subscriber:', err);
      }
    });
  }, []);

  const notifyHeartbeatSubscribers = useCallback((event: SSEHeartbeatEvent) => {
    heartbeatSubscribers.current.forEach(callback => {
      try {
        callback(event);
      } catch (err) {
        console.error('Error in heartbeat subscriber:', err);
      }
    });
  }, []);

  const notifyErrorSubscribers = useCallback((err: Error) => {
    errorSubscribers.current.forEach(callback => {
      try {
        callback(err);
      } catch (errMsg) {
        console.error('Error in error subscriber:', errMsg);
      }
    });
  }, []);

  const connect = useCallback(async () => {
    try {
      setIsConnecting(true);
      setError(undefined);
      
      if (!userRef.current) {
        throw new Error('User not authenticated');
      }

      const token = await userRef.current.getIdToken(true);
      
      if (!token) {
        throw new Error('Failed to get authentication token');
      }

      // Connect to the media processing SSE endpoint
      await eventSource.connect(connectionId, `${CONFIG.serverUrl}sse/listen`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        onOpen: (response) => {
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          setIsConnected(true);
          setIsConnecting(false);
        },
        onMessage: (event) => {
          if (!event.data) {
            return;
          }

          try {
            const payload = JSON.parse(event.data);
            const eventType = event.event as SSEEventType;

            switch (eventType) {
              case 'connected': {
                const connectedEvent = payload as SSEConnectedEvent;
                setConnectionInfo(connectedEvent);
                notifyConnectedSubscribers(connectedEvent);
                break;
              }

              case 'media_update': {
                const mediaUpdate = payload as MediaProcessingUpdate;
                setMediaUpdates(prev => [mediaUpdate, ...prev]);
                
                // Handle specific media update types
                switch (mediaUpdate.name) {
                  case 'place':
                    mutatePlaceList(orgGuid);
                    break;
                  default:
                    break;
                }
                
                notifyMediaUpdateSubscribers(mediaUpdate);
                break;
              }

              case 'heartbeat': {
                const heartbeatEvent = payload as SSEHeartbeatEvent;
                notifyHeartbeatSubscribers(heartbeatEvent);
                break;
              }

              default:
                break;
            }
          } catch (err) {
            console.error('Bad SSE payload:', err);
            const errorMsg = err instanceof Error ? err : new Error(String(err));
            notifyErrorSubscribers(errorMsg);
          }
        },
        onError: (err) => {
          console.error('SSE error:', err);
          setIsConnected(false);
          setIsConnecting(false);
          setError(err);
          notifyErrorSubscribers(err);
          throw err; // This triggers automatic reconnect
        },
        onClose: () => {
          setIsConnected(false);
          setIsConnecting(false);
        },
      });

      reconnectAttemptsRef.current = 0; // Reset reconnect attempts on successful connection
    } catch (err) {
      console.error('Failed to connect to media processing SSE:', err);
      const errorMsg = err instanceof Error ? err : new Error(String(err));
      setError(errorMsg);
      setIsConnected(false);
      setIsConnecting(false);
      notifyErrorSubscribers(errorMsg);
    }
  }, [eventSource, connectionId, orgGuid, notifyMediaUpdateSubscribers, notifyConnectedSubscribers, notifyHeartbeatSubscribers, notifyErrorSubscribers]);

  // Subscription methods
  const subscribe = useCallback((callback: (update: MediaProcessingUpdate) => void) => {
    mediaUpdateSubscribers.current.add(callback);
    return () => {
      mediaUpdateSubscribers.current.delete(callback);
    };
  }, []);

  const subscribeToConnected = useCallback((callback: (event: SSEConnectedEvent) => void) => {
    connectedSubscribers.current.add(callback);
    return () => {
      connectedSubscribers.current.delete(callback);
    };
  }, []);

  const subscribeToHeartbeat = useCallback((callback: (event: SSEHeartbeatEvent) => void) => {
    heartbeatSubscribers.current.add(callback);
    return () => {
      heartbeatSubscribers.current.delete(callback);
    };
  }, []);

  const subscribeToError = useCallback((callback: (error: Error) => void) => {
    errorSubscribers.current.add(callback);
    return () => {
      errorSubscribers.current.delete(callback);
    };
  }, []);

  // Auth state listener
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      userRef.current = user;
      isAuthReadyRef.current = true;
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    if (isAuthReadyRef.current && userRef.current) {
      connect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthReadyRef.current, userRef.current, connect]);

  // Reconnection logic
  useEffect(() => {
    // Clear any existing timeout when effect runs
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = undefined;
    }

    const connection = eventSource.getConnection(connectionId);
    const connectionError = connection?.error || error;
    
    // Only attempt reconnection for certain types of errors (not network resolution errors)
    // and only if user is authenticated and not already connected/connecting
    const shouldReconnect = connectionError && 
      userRef.current && // Only reconnect if user is authenticated
      !isConnected && // Don't reconnect if already connected
      !isConnecting && // Don't reconnect if already connecting
      reconnectAttemptsRef.current < maxReconnectAttempts &&
      !connectionError.message?.includes('ERR_NAME_NOT_RESOLVED') &&
      !connectionError.message?.includes('Failed to fetch');
    
    if (shouldReconnect) {
      reconnectAttemptsRef.current += 1;
      
      reconnectTimeoutRef.current = setTimeout(() => {
        connect();
      }, reconnectInterval);
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [error, isConnected, isConnecting, eventSource, connectionId, reconnectInterval, maxReconnectAttempts, connect]); // Include error, connection state, and connect to trigger on error changes

  const value = useMemo<MediaUpdatesContextType>(() => ({
    isConnected,
    isConnecting,
    connectionInfo,
    error,
    user: userRef.current,
    isAuthReady: isAuthReadyRef.current,
    mediaUpdates,
    connect,
    subscribe,
    subscribeToConnected,
    subscribeToHeartbeat,
    subscribeToError,
  }), [
    isConnected,
    isConnecting,
    connectionInfo,
    error,
    mediaUpdates,
    connect,
    subscribe,
    subscribeToConnected,
    subscribeToHeartbeat,
    subscribeToError,
  ]);

  return (
    <MediaUpdatesContext.Provider value={value}>
      {children}
    </MediaUpdatesContext.Provider>
  );
}

// ----------------------------------------------------------------------

export function useMediaUpdates() {
  const context = useContext(MediaUpdatesContext);
  if (!context) {
    throw new Error('useMediaUpdates must be used within a MediaUpdatesProvider');
  }
  return context;
}

// ----------------------------------------------------------------------

/**
 * Hook for subscribing to media updates
 */
export function useMediaUpdatesSubscription(
  onUpdate: (update: MediaProcessingUpdate) => void,
  deps: React.DependencyList = []
) {
  const { subscribe } = useMediaUpdates();
  
  useEffect(() => {
    const unsubscribe = subscribe(onUpdate);
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribe, ...deps]);
}

/**
 * Hook for subscribing to connection events
 */
export function useMediaUpdatesConnection(
  onConnected?: (event: SSEConnectedEvent) => void,
  onError?: (error: Error) => void,
  deps: React.DependencyList = []
) {
  const { subscribeToConnected, subscribeToError } = useMediaUpdates();
  
  useEffect(() => {
    const unsubscribeConnected = onConnected ? subscribeToConnected(onConnected) : () => {};
    const unsubscribeError = onError ? subscribeToError(onError) : () => {};
    
    return () => {
      unsubscribeConnected();
      unsubscribeError();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribeToConnected, subscribeToError, ...deps]);
}

/**
 * Hook for subscribing to heartbeat events
 */
export function useMediaUpdatesHeartbeat(
  onHeartbeat: (event: SSEHeartbeatEvent) => void,
  deps: React.DependencyList = []
) {
  const { subscribeToHeartbeat } = useMediaUpdates();
  
  useEffect(() => {
    const unsubscribe = subscribeToHeartbeat(onHeartbeat);
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribeToHeartbeat, ...deps]);
}
